import { Container, Grid, Typography } from "@material-ui/core";
import { useSelector } from "react-redux";
import AppNewCustomer from "src/components/estadisticas/app-new-customer";
import AppTaskFinished from "src/components/estadisticas/app-task-finished";
import AppWidgetFacturacion from "src/components/estadisticas/app-widget-facturacion";
import { informesApi } from "src/services/informesApi";
import { empleadosApi } from "src/services/empleadosApi";
import { clientesApi } from "src/services/clientesApi";
import { tareasApi } from "src/services/tareasApi";
import { capitalizarPrimeraLetra } from "src/utils/sharedFunctions";
import dayjs from "dayjs";
import arrowLeftDown from "@iconify/icons-eva/diagonal-arrow-left-down-fill";
import arrowRightUp from "@iconify/icons-eva/diagonal-arrow-right-up-fill";
import facturacion from "@iconify/icons-ic/outline-receipt-long";
import recaudacion from "@iconify/icons-ant-design/dollar-circle-outlined";
import AppDetalleClientes from "src/components/estadisticas/app-detalle-clientes";
import AppWidgetIcon from "src/components/estadisticas/app-widgets-icon";
import AppHistorialTareas from "src/components/estadisticas/app-historial-tareas";
import { useCallback, useEffect, useMemo, useState } from "react";
import ModalEstadisticasFacturacionMensual from "src/components/Modal/modalEstadisticasFacturacion";
import AppTablaEmpleadosActivos from "src/components/estadisticas/app-tabla-empleados-activos";
import ModalDetalleTareaSucursal from "src/components/Modal/modalDetalleTareasSucursal";
import ModalDetalleTareaPorEstado from "src/components/Modal/modalDetalleTareaPorEstado";
import { sucursalesApi } from "src/services/sucursalesApi";
import ModalDistribucionClientes from "src/components/Modal/modalDistribucionCliente";
import { planesApi } from "src/services/planesApi";
import { facturasApi } from "src/services/facturasApi";
import AppDistribucionPropuestas from "src/components/estadisticas/app-distribucion-propuestas";
import { propuestasApi } from "src/services/propuestasApi";
import ModalDetalleResumenPropuestas from "src/components/Modal/modalDetalleResumenPropuestas";
import DateRangeFilter from "src/components/estadisticas/date-range-filter";
import SucursalFilter from "src/components/estadisticas/sucursal-filter";
import { formatearFecha } from "src/utils/formatTime";
import AppFacturasSucursales from "src/components/informes/App-facturas-sucursales";
import { useSnackbar } from "src/components/snackbar";
import AppKpiEficienciaCobranza from "src/components/estadisticas/app-kpi-eficiencia-cobranza";
import AppKpiCumplimientoTareas from "src/components/estadisticas/app-kpi-cumplimiento-tareas";
import AppKpiMorosidad from "src/components/estadisticas/app-kpi-morosidad";
import ModalDistribucionEmpleados from "src/components/Modal/modalDistribucionEmpleados";
import ModalEstadisticasRecaudacion from "src/components/Modal/modalEstadisticasRecaudacion";

const getStorageKey = (rut) => ({
  sucursales: `estadisticas_sucursales_${rut}`,
  fechaInicio: `estadisticas_fecha_inicio_${rut}`,
  fechaFin: `estadisticas_fecha_fin_${rut}`,
});

/**
 * Valida si un string recuperado del localStorage es una fecha real en formato YYYY/MM/DD.
 * Evita que valores parciales como "2" o "202" sean procesados como fechas.
 */
const esDateValida = (fechaString) => {
  if (!fechaString || typeof fechaString !== "string") return false;
  return dayjs(fechaString, "YYYY/MM", true).isValid();
  //solo acepte fechas completas
};

const Estadisticas = () => {
  const {
    empleado: usuario,
    sucursal: { refSucursal: sucursalEmpleado },
  } = useSelector((state) => state.auth.user);
  const { enqueueSnackbar } = useSnackbar();

  const [sucursal, setSucursal] = useState("");
  const [modalEstadisticas, setModalEstadisticas] = useState(false);
  const [modalDetalleTareaSucursal, setModalDetalleTareaSucursal] =
    useState(false);
  const [
    abrirModalDetalleResumenPropuestas,
    setAbrirModalDetalleResumenPropuestas,
  ] = useState(false);

  const [abrirModalDetalleTareaPorEstado, setAbrirModalDetalleTareaPorEstado] =
    useState(false);

  const { data: sucursalesEmpleado, isFetching: obteniendoSucursalesEmpleado } =
    sucursalesApi.useGetSucursalesEmpleadosQuery(usuario.rut);
  const [
    obtenerPropuestasFiltradasPorEstado,
    { data: propuestasFiltradas, isFetching: cargandoPropuestasFiltradas },
  ] = propuestasApi.useLazyGetPropuestasFiltradasPorEstadoQuery();

  //// Evita recrear valores en cada render (mejor rendimiento y menos efectos innecesarios)
  const fechaHoy = useMemo(() => dayjs(), []);
  const finDeMes = useMemo(() => dayjs().endOf("month"), []);
  const STORAGE_KEYS = useMemo(() => getStorageKey(usuario.rut), [usuario.rut]);

  const cargarDesdeStorage = useCallback(() => {
    try {
      const sucursalesGuardadas = localStorage.getItem(STORAGE_KEYS.sucursales);
      const fechaInicioGuardada = localStorage.getItem(
        STORAGE_KEYS.fechaInicio,
      );
      const fechaFinGuardada = localStorage.getItem(STORAGE_KEYS.fechaFin);

      // Validar antes de usar: si el valor guardado no es una fecha real se descarta
      // y se limpia el storage para evitar que valores parciales (ej: "2", "202") persistan.
      const inicioValido = esDateValida(fechaInicioGuardada);
      const finValido = esDateValida(fechaFinGuardada);

      if (!inicioValido && fechaInicioGuardada) {
        console.warn(
          `Fecha inicio inválida en storage: "${fechaInicioGuardada}". Se usará la fecha por defecto.`,
        );
        localStorage.removeItem(STORAGE_KEYS.fechaInicio);
      }
      if (!finValido && fechaFinGuardada) {
        console.warn(
          `Fecha fin inválida en storage: "${fechaFinGuardada}". Se usará la fecha por defecto.`,
        );
        localStorage.removeItem(STORAGE_KEYS.fechaFin);
      }

      return {
        sucursales: sucursalesGuardadas
          ? JSON.parse(sucursalesGuardadas)
          : [sucursalEmpleado],
        fechaInicio: inicioValido
          ? dayjs(fechaInicioGuardada, "YYYY/MM/DD").toDate()
          : dayjs().startOf("month").toDate(),
        fechaFin: finValido
          ? dayjs(fechaFinGuardada, "YYYY/MM/DD").toDate()
          : (finDeMes.isAfter(fechaHoy) ? fechaHoy : finDeMes).toDate(),
      };
    } catch (error) {
      console.warn("Error al cargar preferencias:", error);
      return {
        sucursales: [sucursalEmpleado],
        fechaInicio: dayjs().startOf("month").toDate(),
        fechaFin: (finDeMes.isAfter(fechaHoy) ? fechaHoy : finDeMes).toDate(),
      };
    }
  }, [STORAGE_KEYS, sucursalEmpleado, finDeMes, fechaHoy]);

  // Memo para no leer localStorage en cada render.
  const preferenciasGuardadas = useMemo(
    () => cargarDesdeStorage(),
    [cargarDesdeStorage],
  );

  // Nuevos estados para filtros
  const [fechaInicio, setFechaInicio] = useState(
    preferenciasGuardadas.fechaInicio,
  );

  const [fechaFin, setFechaFin] = useState(preferenciasGuardadas.fechaFin);
  const [sucursalesSeleccionadas, setSucursalesSeleccionadas] = useState(
    preferenciasGuardadas.sucursales,
  );

  const [
    tituloModalDetalleTareasPorEstado,
    setTituloModalDetalleTareasPorEstado,
  ] = useState("");
  const [tituloDetalleResumenPropuestas, setTituloDetalleResumenPropuestas] =
    useState("");
  const [abrirModalDetalleClientes, setAbrirModalDetalleClientes] =
    useState(false);
  const [abrirModalDetalleEmpleados, setAbrirModalDetalleEmpleados] =
    useState(false);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState("");
  const [fechaInicioExportacion, setFechaInicioExportacion] = useState("");
  const [fechaFinExportacion, setFechaFinExportacion] = useState("");
  const [modalIngresos, setModalIngresos] = useState(false);

  const obtenerSucursalesParaFiltro = useCallback(() => {
    // Si hay sucursales seleccionadas, usarlas
    if (sucursalesSeleccionadas.length > 0) {
      return sucursalesSeleccionadas;
    }
    // Si no hay seleccionadas Y tenemos datos de sucursales, usar todas
    if (sucursalesEmpleado && sucursalesEmpleado.length > 0) {
      return sucursalesEmpleado.map((s) => s.refSucursal);
    }
    // Fallback vacío
    return [];
  }, [sucursalesSeleccionadas, sucursalesEmpleado]);

  // Compatibilidad temporal - mantener filtroFechaEstadisticas actualizado
  const filtroFechaEstadisticas = useMemo(() => {
    return dayjs(fechaInicio).format("YYYY/MM");
  }, [fechaInicio]);

  const { data: empleadosActivos = [], isFetching: cargandoEmpleadosActivos } =
    empleadosApi.useObtenerEmpleadosActivosSucursalQuery(
      {
        rut: usuario.rut,
        sucursales: obtenerSucursalesParaFiltro(),
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const {
    data: historialTareas,
    isFetching: realizandoPeticionHistorialTareas,
  } = tareasApi.useHistorialUltimasTareasFinalizadasQuery(
    { rut: usuario.rut, sucursales: obtenerSucursalesParaFiltro() },
    {
      pollingInterval: 900000, // Cada 15 minutos hace la petición.
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: resumenPropuestasSucursales,
    isFetching: cargandoResumenPropuestasSucursales,
  } = propuestasApi.useGetResumenPorEstadoYSucursalQuery(
    {
      rut: usuario.rut,
      fecha: dayjs(fechaFin).format("YYYY/MM"),
      fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
      fechaFin: dayjs(fechaFin).format("YYYY/MM"),
      sucursales: obtenerSucursalesParaFiltro(),
    },
    { skip: !usuario.rut, refetchOnMountOrArgChange: true },
  );

  const [
    obtenerDetalleCantidadTareas,
    { data: detalleCantidadTareas, isFetching: cargandoDetalleCantidadTareas },
  ] = tareasApi.useLazyGetCantidadTareasMensualesYTotalesQuery();

  const [
    obtenerTareasAnualesYMensualesAgrupadas,
    {
      data: tareasMensualesYAnualesAgrupadas,
      isFetching: cargandoTareasMensualesYAnualesAgrupadas,
    },
  ] = tareasApi.useLazyGetTareasAnualesYMensualesAgrupadasQuery();

  const [
    obtenerInformacionTareasInternas,
    {
      data: informacionTareasInternas,
      isFetching: cargandoInformacionTareasInternas,
    },
  ] = tareasApi.useLazyGetTareasInformacionQuery();

  // const {
  //   data: tareasMasAntiguas,
  //   isFetching: realizandoPeticionTareasMasAntiguas,
  // } = tareasApi.useTareasPendientesMasAntiguasQuery(undefined, {
  //   pollingInterval: 900000, // Cada 15 minutos hace la petición.
  // });

  const {
    data: ultimosClientesGestor,
    isFetching: realizandoPeticionUltimosClientes,
  } = clientesApi.useUltimosClientesGestorQuery(
    { rut: usuario.rut, sucursales: obtenerSucursalesParaFiltro() },
    {
      pollingInterval: 1800000, // Cada 30 minutos hace la petición.
      refetchOnMountOrArgChange: true,
    },
  );

  const [
    obtenerEstadisticasTareas,
    {
      data: estadisticasTareasEmpleado,
      isFetching: obteniendoEstadisticasTareasEmpleado,
    },
  ] = tareasApi.useLazyObtenerTareasTotalesEmpleadosSucursalQuery();

  const { data: cantidadEmpleados, isLoading: relizandoPeticionEmpleados } =
    empleadosApi.useObtenerEmpleadosPorSucursalQuery(
      {
        rut: usuario.rut,
        sucursales: obtenerSucursalesParaFiltro(),
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const {
    data: cantidadClientesActivos,
    isFetching: realizandoPeticionClientes,
  } = clientesApi.useCantidadClientesActivosConFiltrosQuery({
    rut: usuario.rut,
    sucursales: obtenerSucursalesParaFiltro(),
    fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
    fechaFin: dayjs(fechaFin).format("YYYY/MM"),
  });

  const {
    data: clientesPorDivision,
    isFetching: realizandoPeticionClientesPorDivision,
  } = clientesApi.useObtenerClientesPorDivisionQuery({
    rut: usuario.rut,
    sucursales: obtenerSucursalesParaFiltro(),
    fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
    fechaFin: dayjs(fechaFin).format("YYYY/MM"),
  });

  const { data: infoRecuentoTareas, isFetching: obteniendoRecuentoTareas } =
    informesApi.useGetInformacionRecuentoTareasQuery(
      {
        rut: usuario.rut,
        sucursales: obtenerSucursalesParaFiltro(),
        fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
        fechaFin: dayjs(fechaFin).format("YYYY/MM"),
      },
      { skip: !usuario.rut, refetchOnMountOrArgChange: true },
    );

  const { data: facturacionDatos, isFetching: realizandoPeticionFacturacion } =
    informesApi.useObtenerRecaudacionesYFacturasTotalesQuery(
      {
        rut: usuario.rut,
        fechaTermino: filtroFechaEstadisticas,
        fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
        fechaFin: dayjs(fechaFin).format("YYYY/MM"),
        sucursales: obtenerSucursalesParaFiltro(),
      },
      {
        pollingInterval: 900000,
        refetchOnMountOrArgChange: true,
      },
    );

  const {
    data: kpiEficienciaCobranza,
    isFetching: cargandoKpiEficienciaCobranza,
    isError: errorKpiEficienciaCobranza,
  } = informesApi.useGetKpiEficienciaCobranzaQuery(
    {
      fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
      fechaFin: dayjs(fechaFin).format("YYYY/MM"),
      sucursales: obtenerSucursalesParaFiltro(),
    },
    {
      skip: !usuario.rut,
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: kpiCumplimientoTareas,
    isFetching: cargandoKpiCumplimientoTareas,
    isError: errorKpiCumplimientoTareas,
  } = informesApi.useGetKpiCumplimientoTareasQuery(
    {
      fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
      fechaFin: dayjs(fechaFin).format("YYYY/MM"),
      sucursales: obtenerSucursalesParaFiltro(),
    },
    {
      skip: !usuario.rut,
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: kpiMorosidad,
    isFetching: cargandoKpiMorosidad,
    isError: errorKpiMorosidad,
  } = informesApi.useGetKpiMorosidadQuery(
    {
      fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
      fechaFin: dayjs(fechaFin).format("YYYY/MM"),
      sucursales: obtenerSucursalesParaFiltro(),
    },
    {
      skip: !usuario.rut,
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: datosFacturacionPorSucursales,
    isFetching: cargandoDatosFacturacionPorSucursales,
  } = informesApi.useGetInformacionfacturacionPorSucursalesConFechaQuery(
    {
      rut: usuario.rut,
      sucursales: obtenerSucursalesParaFiltro(),
      fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
      fechaFin: dayjs(fechaFin).format("YYYY/MM"),
    },
    {
      skip: !usuario.rut,
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: tareasEspecialesRecientes,
    isFetching: realizandoPeticionTareasEspecialesRecientes,
  } = tareasApi.useHistorialUltimasTareasEspecialesIngresadasQuery(
    { rut: usuario.rut, sucursales: obtenerSucursalesParaFiltro() },
    {
      pollingInterval: 900000,
      refetchOnMountOrArgChange: true,
    },
  );

  const [
    obtenerTareasTareasEmpleados,
    { data: tareasEmpleado, isFetching: obteniendoTareasEmpleado },
  ] = tareasApi.useLazyGetTareasTotalesPorSucursalTipoTareaYFechaQuery();

  const [
    obtenerDispersionEmpleados,
    {
      data: dispersionTareasEmpleado,
      isFetching: obteniendoDispersionTareasEmpleado,
    },
  ] = tareasApi.useLazyGetCantidadTareasEmpleadosAgrupadasQuery();

  const [
    obtenerCantidadPromedioPlanes,
    {
      data: cantidadPlanesPromedioClientes,
      isFetching: obteniendoCantidadPlanesPromedioClientes,
    },
  ] = planesApi.useLazyObtenerCantidadPromedioPlanesClienteQuery();

  const [
    obtenerPromedioFacturasClientes,
    {
      data: promedioFacturasClientes,
      isFetching: obteniendoPromedioFacturasCliente,
    },
  ] = facturasApi.useLazyObtenerPromedioFacturasEmitidasPorClienteQuery();

  const [
    obtenerClientesPorDivision,
    {
      data: clientesPorDivisionLazy,
      isFetching: obteniendoclientesPorDivision,
    },
  ] = clientesApi.useLazyObtenerDistribucionDeClientesQuery();

  const [
    obtenerPlanesMasCotizados,
    { data: planesMasCotizados, isFetching: obteniendoPlanesMasCotizados },
  ] = planesApi.useLazyObtenerPlanesMasCotizadosQuery();

  const [
    obtenerContribuyentesPlanes,
    { data: contribuyentesPlanes, isFetching: obteniendocontribuyentesPlanes },
  ] = planesApi.useLazyObtenerContribuyentesPlanesQuery();

  const handleMostrarDetalleFacturacion = useCallback(() => {
    setModalEstadisticas(true);
  }, [setModalEstadisticas]);

  const guardarSucursalesEnStorage = useCallback(
    (sucursales) => {
      try {
        localStorage.setItem(
          STORAGE_KEYS.sucursales,
          JSON.stringify(sucursales),
        );
      } catch (error) {
        console.warn("Error al guardar sucursales:", error);
      }
    },
    [STORAGE_KEYS],
  );

  // Verifica que la fecha esté bien hecha (evita años raros) y se declara antes para no causar errores.
  const esDateObjectValido = useCallback((fecha) => {
    if (!fecha || !(fecha instanceof Date) || isNaN(fecha.getTime()))
      return false;
    const año = fecha.getFullYear();
    return año >= 1900;
  }, []);

  const guardarFechasEnStorage = useCallback(
    (inicio, fin) => {
      try {
        // Solo guardar si ambas fechas son objetos válidos para evitar
        // persistir valores nulos o inválidos que corrompan el storage.
        if (!esDateObjectValido(inicio) || !esDateObjectValido(fin)) {
          console.warn(
            "guardarFechasEnStorage: se omitió el guardado por fecha inválida.",
          );
          return;
        }
        localStorage.setItem(
          STORAGE_KEYS.fechaInicio,
          dayjs(inicio).format("YYYY/MM/DD"),
        );
        localStorage.setItem(
          STORAGE_KEYS.fechaFin,
          dayjs(fin).format("YYYY/MM/DD"),
        );
      } catch (error) {
        console.warn("Error al guardar fechas:", error);
      }
    },
    [STORAGE_KEYS, esDateObjectValido],
  );

  const handleSetSucursalesSeleccionadas = useCallback(
    (sucursales) => {
      setSucursalesSeleccionadas(sucursales);
      guardarSucursalesEnStorage(sucursales);
    },
    [guardarSucursalesEnStorage],
  );

  const handleSetFechaInicio = useCallback(
    (fecha) => {
      if (!esDateObjectValido(fecha)) return;
      setFechaInicio(fecha);
    },
    [esDateObjectValido],
  );

  const handleSetFechaFin = useCallback(
    (fecha) => {
      if (!esDateObjectValido(fecha)) return;
      setFechaFin(fecha);
    },
    [esDateObjectValido],
  );

  // Valida que el rango de fechas tenga sentido y evita ejecuciones innecesarias del effect.
  useEffect(() => {
    // Usar esDateObjectValido (no dayjs().isValid()) porque Date objects
    // siempre retornan isValid()=true incluso con años extraños (ej: 202).
    const inicioValido = esDateObjectValido(fechaInicio);
    const finValido = esDateObjectValido(fechaFin);

    // Guard doble: si por alguna razón llega algo inválido no actuar
    if (!inicioValido || !finValido) return;

    if (dayjs(fechaInicio).isAfter(dayjs(fechaFin))) {
      setFechaFin(fechaInicio);
      enqueueSnackbar("La fecha de término se ajustó al inicio del rango", {
        variant: "info",
      });
      return;
    }

    // Rango válido: persistir en storage
    guardarFechasEnStorage(fechaInicio, fechaFin);
  }, [
    fechaInicio,
    fechaFin,
    esDateObjectValido,
    guardarFechasEnStorage,
    enqueueSnackbar,
  ]);

  const manejarDatosModalDetalleTareasSucursal = useCallback(
    (sucursal) => {
      if (filtroFechaEstadisticas && sucursal) {
        const body = {
          sucursal,
          fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
          fechaFin: dayjs(fechaFin).format("YYYY/MM"),
          tipoTarea: "INTERNA",
        };
        obtenerEstadisticasTareas(body);
        obtenerTareasAnualesYMensualesAgrupadas(body);
        obtenerDetalleCantidadTareas(body);
        obtenerInformacionTareasInternas(body);
      }
    },
    [
      fechaFin,
      fechaInicio,
      filtroFechaEstadisticas,
      obtenerDetalleCantidadTareas,
      obtenerEstadisticasTareas,
      obtenerInformacionTareasInternas,
      obtenerTareasAnualesYMensualesAgrupadas,
    ],
  );

  const modalDetalleTareasSucursales = useCallback(
    (sucursal) => {
      setModalDetalleTareaSucursal(true);
      manejarDatosModalDetalleTareasSucursal(sucursal);
      setSucursal(capitalizarPrimeraLetra(sucursal));
    },
    [manejarDatosModalDetalleTareasSucursal],
  );

  const abrirModalEstadisticasTareasEmpleado = useCallback(
    ({ sucursal, estadoTarea, tipoTarea }) => {
      const tipoTareaLowerCase = tipoTarea.toLowerCase();

      // Determinar el título de la tarea en base al tipo
      const tituloTipoTarea =
        tipoTareaLowerCase === "especial"
          ? "especiales"
          : tipoTareaLowerCase === "planes"
            ? "de planes"
            : tipoTareaLowerCase;
      const titulo = `Tareas ${tituloTipoTarea} ${estadoTarea.toLowerCase()} sucursal ${sucursal}`;

      // Determinar los tipos de tarea en base al tipo de tarea
      const tiposTarea =
        tipoTareaLowerCase === "planes"
          ? ["MES", "AÑO"]
          : tipoTareaLowerCase === "internas"
            ? ["INTERNA"]
            : ["ESPECIAL"];

      // Crear el objeto `body` con los datos necesarios
      const body = {
        sucursal,
        fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
        fechaFin: dayjs(fechaFin).format("YYYY/MM"),
        estadoTarea:
          estadoTarea.toUpperCase() === "PENDIENTES"
            ? "PENDIENTE"
            : estadoTarea.toUpperCase(),
        tipoTarea: tiposTarea,
      };

      // Llamadas a las funciones de obtención de datos
      obtenerTareasTareasEmpleados(body);
      obtenerDispersionEmpleados(body);

      // Actualizar el estado del componente
      setTituloModalDetalleTareasPorEstado(titulo);
      setAbrirModalDetalleTareaPorEstado(true);
    },
    [
      fechaFin,
      fechaInicio,
      obtenerDispersionEmpleados,
      obtenerTareasTareasEmpleados,
    ],
  );

  const manejarClicTarjeta = useCallback(
    (tipoTarjeta) => {
      if (tipoTarjeta.toLowerCase() === "clientes") {
        const filtrosParaModal = {
          rut: usuario.rut,
          sucursales: obtenerSucursalesParaFiltro(),
          fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
          fechaFin: dayjs(fechaFin).format("YYYY/MM"),
        };

        obtenerCantidadPromedioPlanes(filtrosParaModal);
        obtenerPromedioFacturasClientes(filtrosParaModal);
        obtenerClientesPorDivision(filtrosParaModal);
        obtenerPlanesMasCotizados(filtrosParaModal);
        obtenerContribuyentesPlanes(filtrosParaModal);

        setAbrirModalDetalleClientes(true);
      } else if (tipoTarjeta.toLowerCase() === "empleados") {
        setAbrirModalDetalleEmpleados(true);
      } else {
        throw new Error("Opción de tarjeta no válida");
      }
    },
    [
      usuario.rut,
      obtenerSucursalesParaFiltro,
      fechaInicio,
      fechaFin,
      obtenerCantidadPromedioPlanes,
      obtenerPromedioFacturasClientes,
      obtenerClientesPorDivision,
      obtenerPlanesMasCotizados,
      obtenerContribuyentesPlanes,
    ],
  );

  const manejarClicDetalleResumenPropuestas = useCallback(
    (estado) => {
      const fechaInicioAux = dayjs(fechaInicio).format("YYYY/MM");
      const fechaFinAux = dayjs(fechaFin).format("YYYY/MM");
      let titulo;
      if (fechaInicioAux === fechaFinAux) {
        titulo = `Detalle propuestas ${
          estado === "ACEPTADO_CLIENTE" ? "aceptadas" : estado.toLowerCase()
        } - ${formatearFecha(fechaInicioAux, "MMMM YYYY")}`;
      } else {
        titulo = `Detalle propuestas ${
          estado === "ACEPTADO_CLIENTE" ? "aceptadas" : estado.toLowerCase()
        } - ${formatearFecha(fechaInicioAux, "MMMM YYYY")} a ${formatearFecha(
          fechaFin,
          "MMMM YYYY",
        )}`;
      }
      const body = {
        estado,
        sucursales: obtenerSucursalesParaFiltro(),
        fechaInicio: fechaInicioAux,
        fechaFin: fechaFinAux,
        rut: usuario.rut,
      };
      // Usar la nueva función que crearemos para obtener propuestas filtradas
      obtenerPropuestasFiltradasPorEstado(body);
      setTituloDetalleResumenPropuestas(titulo);

      // Guardar datos adicionales para exportación
      setEstadoSeleccionado(estado);
      setFechaInicioExportacion(fechaInicioAux);
      setFechaFinExportacion(fechaFinAux);

      setAbrirModalDetalleResumenPropuestas(true);
    },
    [
      fechaFin,
      fechaInicio,
      usuario.rut,
      obtenerPropuestasFiltradasPorEstado,
      obtenerSucursalesParaFiltro,
    ],
  );

  const tituloFacturacion = useMemo(() => {
    const fechaInicioMoment = dayjs(fechaInicio);
    const fechaFinMoment = dayjs(fechaFin);

    if (fechaInicioMoment.isSame(fechaFinMoment, "month")) {
      // Mismo mes
      return `Facturación ${fechaInicioMoment.format("MMMM YYYY")}`;
    } else if (fechaInicioMoment.isSame(fechaFinMoment, "year")) {
      // Mismo año, diferentes meses
      const inicioMes = fechaInicioMoment.format("MMM");
      const finMes = fechaFinMoment.format("MMM");
      const año = fechaInicioMoment.format("YYYY");
      return `Facturación ${inicioMes} - ${finMes} ${año}`;
    } else {
      // Años diferentes
      const inicio = fechaInicioMoment.format("MMM YYYY");
      const fin = fechaFinMoment.format("MMM YYYY");
      return `Facturación ${inicio} - ${fin}`;
    }
  }, [fechaInicio, fechaFin]);

  const tituloIngresos = useMemo(() => {
    const fechaInicioMoment = dayjs(fechaInicio);
    const fechaFinMoment = dayjs(fechaFin);

    if (fechaInicioMoment.isSame(fechaFinMoment, "month")) {
      return `Ingresos ${fechaInicioMoment.format("MMMM YYYY")}`;
    } else if (fechaInicioMoment.isSame(fechaFinMoment, "year")) {
      const inicioMes = fechaInicioMoment.format("MMM");
      const finMes = fechaFinMoment.format("MMM");
      const año = fechaInicioMoment.format("YYYY");
      return `Ingresos ${inicioMes} - ${finMes} ${año}`;
    } else {
      const inicio = fechaInicioMoment.format("MMM YYYY");
      const fin = fechaFinMoment.format("MMM YYYY");
      return `Ingresos ${inicio} - ${fin}`;
    }
  }, [fechaInicio, fechaFin]);

  const tituloFacturacionSucursales = useMemo(() => {
    const fechaInicioMoment = dayjs(fechaInicio);
    const fechaFinMoment = dayjs(fechaFin);

    if (fechaInicioMoment.isSame(fechaFinMoment, "month")) {
      return `Facturación por sucursal - ${fechaInicioMoment.format(
        "MMMM YYYY",
      )}`;
    } else if (fechaInicioMoment.isSame(fechaFinMoment, "year")) {
      const inicioMes = fechaInicioMoment.format("MMM");
      const finMes = fechaFinMoment.format("MMM");
      const año = fechaInicioMoment.format("YYYY");
      return `Facturación por sucursal - ${inicioMes} - ${finMes} ${año}`;
    } else {
      const inicio = fechaInicioMoment.format("MMM YYYY");
      const fin = fechaFinMoment.format("MMM YYYY");
      return `Facturación por sucursal - ${inicio} - ${fin}`;
    }
  }, [fechaInicio, fechaFin]);

  const tituloRecaudacionSucursales = useMemo(() => {
    const fechaInicioMoment = dayjs(fechaInicio);
    const fechaFinMoment = dayjs(fechaFin);

    if (fechaInicioMoment.isSame(fechaFinMoment, "month")) {
      return `Recaudación por sucursal - ${fechaInicioMoment.format(
        "MMMM YYYY",
      )}`;
    } else if (fechaInicioMoment.isSame(fechaFinMoment, "year")) {
      const inicioMes = fechaInicioMoment.format("MMM");
      const finMes = fechaFinMoment.format("MMM");
      const año = fechaInicioMoment.format("YYYY");
      return `Recaudación por sucursal - ${inicioMes} - ${finMes} ${año}`;
    } else {
      const inicio = fechaInicioMoment.format("MMM YYYY");
      const fin = fechaFinMoment.format("MMM YYYY");
      return `Recaudación por sucursal - ${inicio} - ${fin}`;
    }
  }, [fechaInicio, fechaFin]);

  const subheaderGraficosSucursales = useMemo(() => {
    const fechaInicioMoment = dayjs(fechaInicio);
    const fechaFinMoment = dayjs(fechaFin);

    if (fechaInicioMoment.isSame(fechaFinMoment, "month")) {
      return `Detalle del mes de ${fechaInicioMoment.format("MMMM YYYY")}`;
    } else {
      return "Detalle del período seleccionado";
    }
  }, [fechaInicio, fechaFin]);

  const subheaderDistribucionPropuestas = useMemo(() => {
    const inicio = dayjs(fechaInicio);
    const fin = dayjs(fechaFin);

    // Guard: si alguna fecha es inválida no intentar formatear
    if (!inicio.isValid() || !fin.isValid()) return "";

    // Se usa esta validación porque dayjs puede decir que una fecha es válida aunque tenga un año incorrecto.
    const inicioStr = capitalizarPrimeraLetra(inicio.format("MMMM YYYY"));

    if (inicio.isSame(fin, "month")) {
      return inicioStr;
    }

    const finStr = capitalizarPrimeraLetra(fin.format("MMMM YYYY"));
    return `${inicioStr} - ${finStr}`;
  }, [fechaInicio, fechaFin]);

  return (
    <>
      <Container maxWidth="xl">
        <Grid
          container
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Grid item xs={12} sm={4} md={4} lg={4}>
            <Typography variant="h4">
              ¡Hola! {capitalizarPrimeraLetra(usuario.nombre)}, Bienvenido/a 👋
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={8}
            md={8}
            lg={8}
            sx={{
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-end" },
              alignItems: "center",
            }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6} md={7} lg={7}>
                <DateRangeFilter
                  id="filtro-rango-fechas"
                  label="Filtrar por rango de fechas"
                  fechaInicio={fechaInicio}
                  fechaFin={fechaFin}
                  onChangeFechaInicio={handleSetFechaInicio}
                  onChangeFechaFin={handleSetFechaFin}
                  maxDate={fechaHoy.toDate()}
                  tamanio={12}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={5} lg={5}>
                <SucursalFilter
                  id="filtro-sucursales"
                  label="Filtrar por sucursales"
                  rutEmpleado={usuario.rut}
                  sucursalesSeleccionadas={sucursalesSeleccionadas}
                  onChangeSucursales={handleSetSucursalesSeleccionadas}
                  tamanio={12}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8} lg={8}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={6}>
                <AppWidgetFacturacion
                  title={tituloFacturacion}
                  color="green"
                  icon={facturacion}
                  mostrarDetalleFacturacion={handleMostrarDetalleFacturacion}
                  mostrarProyeccion={true}
                  cursor="pointer"
                  iconTrending={
                    (!realizandoPeticionFacturacion && facturacionDatos
                      ? facturacionDatos.porcentajeFacturas
                      : 0) < 0
                      ? arrowLeftDown
                      : arrowRightUp
                  }
                  percent={
                    !realizandoPeticionFacturacion && facturacionDatos
                      ? facturacionDatos.porcentajeFacturas
                      : 0
                  }
                  mostrarTrendingInfo={true}
                  loading={realizandoPeticionFacturacion}
                  total={
                    !realizandoPeticionFacturacion && facturacionDatos
                      ? facturacionDatos.facturaTotalMes
                      : 0
                  }
                  chart={{
                    series:
                      !realizandoPeticionFacturacion && facturacionDatos
                        ? facturacionDatos.facturasGrafico.map(
                            (factura) => factura.total,
                          )
                        : [],
                    label:
                      !realizandoPeticionFacturacion && facturacionDatos
                        ? facturacionDatos.facturasGrafico.map(
                            (factura) => factura.fecha,
                          )
                        : [],
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <AppWidgetFacturacion
                  title={tituloIngresos}
                  color="warning"
                  icon={recaudacion}
                  mostrarDetalleFacturacion={() => setModalIngresos(true)}
                  cursor="pointer"
                  iconTrending={-0.5 < 0 ? arrowLeftDown : arrowRightUp}
                  percent={-0.5}
                  loading={realizandoPeticionFacturacion}
                  total={
                    !realizandoPeticionFacturacion && facturacionDatos
                      ? facturacionDatos.recaudadoTotalMes
                      : 0
                  }
                  chart={{
                    series:
                      !realizandoPeticionFacturacion && facturacionDatos
                        ? facturacionDatos.recaudadoGrafico.map(
                            (factura) => factura.total,
                          )
                        : [],
                    label:
                      !realizandoPeticionFacturacion && facturacionDatos
                        ? facturacionDatos.recaudadoGrafico.map(
                            (factura) => factura.fecha,
                          )
                        : [],
                  }}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                <AppWidgetIcon
                  loading={
                    relizandoPeticionEmpleados || realizandoPeticionClientes
                  }
                  onClick={manejarClicTarjeta}
                  series={[
                    {
                      label: "Empleados",
                      total: cantidadEmpleados || 0,
                      clic: true,
                      imagen: "/assets/icons/glass/empleados_icono.svg",
                    },
                    {
                      label: "Clientes",
                      total: cantidadClientesActivos || 0,
                      clic: true,
                      imagen: "/assets/icons/glass/clientes_icono.svg",
                    },
                  ]}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4} lg={4}>
            <AppDetalleClientes
              title="Detalle clientes"
              subheader={
                "Se muestra el detalle de cómo se dividen las tareas y planes entre los clientes"
              }
              loading={realizandoPeticionClientesPorDivision}
              total={
                !realizandoPeticionClientesPorDivision && clientesPorDivision
                  ? clientesPorDivision.totalClientes
                  : 0
              }
              chart={{
                series:
                  !realizandoPeticionClientesPorDivision && clientesPorDivision
                    ? clientesPorDivision.series
                    : [],
                colors: [
                  ["#FF6B6B", "#C92A2A"], // Planes
                  ["#69DB7C", "#37B24D"], // Tareas Especiales
                  ["#74C0FC", "#1C7ED6"], // Ambos
                ],
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Indicadores clave
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Resultados según el período o la fecha de corte indicada en cada
              tarjeta, para las sucursales seleccionadas.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <AppKpiEficienciaCobranza
              data={kpiEficienciaCobranza}
              loading={cargandoKpiEficienciaCobranza}
              error={errorKpiEficienciaCobranza}
            />
          </Grid>

          <Grid item xs={12}>
            <AppKpiCumplimientoTareas
              data={kpiCumplimientoTareas}
              loading={cargandoKpiCumplimientoTareas}
              error={errorKpiCumplimientoTareas}
            />
          </Grid>

          <Grid item xs={12}>
            <AppKpiMorosidad
              data={kpiMorosidad}
              loading={cargandoKpiMorosidad}
              error={errorKpiMorosidad}
            />
          </Grid>

          <Grid item xs={12} md={12} lg={12}>
            <AppDistribucionPropuestas
              title="Distribución de propuestas"
              subheader={subheaderDistribucionPropuestas}
              data={resumenPropuestasSucursales}
              cargandoData={cargandoResumenPropuestasSucursales}
              loadingComponent={cargandoResumenPropuestasSucursales}
              onClick={manejarClicDetalleResumenPropuestas}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <AppFacturasSucursales
              title={tituloFacturacionSucursales}
              subheader={subheaderGraficosSucursales}
              loading={cargandoDatosFacturacionPorSucursales}
              chart={{
                series:
                  !cargandoDatosFacturacionPorSucursales &&
                  datosFacturacionPorSucursales
                    ? datosFacturacionPorSucursales.facturasTotales
                    : [],
              }}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <AppFacturasSucursales
              title={tituloRecaudacionSucursales}
              subheader={subheaderGraficosSucursales}
              loading={cargandoDatosFacturacionPorSucursales}
              chart={{
                series:
                  !cargandoDatosFacturacionPorSucursales &&
                  datosFacturacionPorSucursales
                    ? datosFacturacionPorSucursales.recaudacionesTotales
                    : [],
              }}
            />
          </Grid>

          <Grid item xs={12} md={12} lg={12}>
            <AppHistorialTareas
              title="Recuento tareas"
              subheader="Se muestran las tareas pendientes y en proceso de las sucursales"
              loading={obteniendoRecuentoTareas || obteniendoSucursalesEmpleado}
              abrirModal={modalDetalleTareasSucursales}
              manejarClickGrafico={abrirModalEstadisticasTareasEmpleado}
              sucursales={obtenerSucursalesParaFiltro()}
              sucursalesEmpleado={sucursalesEmpleado}
              chart={{
                data:
                  !obteniendoRecuentoTareas && infoRecuentoTareas
                    ? infoRecuentoTareas
                    : {},
                series: [
                  {
                    type: "Especial",
                    data: [
                      {
                        name: "Pendientes",
                        data:
                          !obteniendoRecuentoTareas && infoRecuentoTareas
                            ? infoRecuentoTareas.tareasEspeciales
                                .filter((tarea) => tarea.estado === "PENDIENTE")
                                .map((tarea) => tarea.cantidad)
                            : [],
                      },
                      {
                        name: "En proceso",
                        data:
                          !obteniendoRecuentoTareas && infoRecuentoTareas
                            ? infoRecuentoTareas.tareasEspeciales
                                .filter(
                                  (tarea) => tarea.estado === "EN PROCESO",
                                )
                                .map((tarea) => tarea.cantidad)
                            : [],
                      },
                    ],
                  },
                  {
                    type: "Planes",
                    data: [
                      {
                        name: "Pendientes",
                        data:
                          !obteniendoRecuentoTareas && infoRecuentoTareas
                            ? infoRecuentoTareas.tareasDePlan
                                .filter((tarea) => tarea.estado === "PENDIENTE")
                                .map((tarea) => tarea.cantidad)
                            : [],
                      },
                      {
                        name: "En proceso",
                        data:
                          !obteniendoRecuentoTareas && infoRecuentoTareas
                            ? infoRecuentoTareas.tareasDePlan
                                .filter(
                                  (tarea) => tarea.estado === "EN PROCESO",
                                )
                                .map((tarea) => tarea.cantidad)
                            : [],
                      },
                    ],
                  },
                  {
                    type: "Internas",
                    data: [
                      {
                        name: "Pendientes",
                        data:
                          !obteniendoRecuentoTareas && infoRecuentoTareas
                            ? infoRecuentoTareas.tareasInternas
                                .filter((tarea) => tarea.estado === "PENDIENTE")
                                .map((tarea) => tarea.cantidad)
                            : [],
                      },
                      {
                        name: "En proceso",
                        data:
                          !obteniendoRecuentoTareas && infoRecuentoTareas
                            ? infoRecuentoTareas.tareasInternas
                                .filter(
                                  (tarea) => tarea.estado === "EN PROCESO",
                                )
                                .map((tarea) => tarea.cantidad)
                            : [],
                      },
                    ],
                  },
                ],
              }}
            />
          </Grid>

          <Grid item xs={12} md={12} lg={12}>
            <AppTablaEmpleadosActivos
              title="Estadísticas empleados"
              subheader="Se pueden revisar las estadísticas de tareas especiales de los empleados"
              rows={!cargandoEmpleadosActivos ? empleadosActivos : []}
              loading={cargandoEmpleadosActivos}
            />
          </Grid>

          {/* <Grid item xs={12} md={12} lg={12}>
            <AppEmployeesTask
              title="Tareas pendientes empleado"
              subheader="Ordenadas por antigüedad"
              rows={
                !realizandoPeticionTareasMasAntiguas && tareasMasAntiguas
                  ? tareasMasAntiguas
                  : []
              }
              loading={realizandoPeticionTareasMasAntiguas}
            />
          </Grid> */}

          <Grid item xs={12} md={12} lg={6}>
            <AppNewCustomer
              title="Últimas tareas especiales incorporadas"
              subheader="Historial de las últimas 5 tareas especiales ingresadas"
              loading={realizandoPeticionTareasEspecialesRecientes}
              list={
                !realizandoPeticionTareasEspecialesRecientes &&
                tareasEspecialesRecientes
                  ? tareasEspecialesRecientes
                  : []
              }
            />
          </Grid>

          <Grid item xs={12} md={12} lg={6}>
            <AppNewCustomer
              title="Últimos clientes incorporados"
              loading={realizandoPeticionUltimosClientes}
              subheader="Expansión de nuestra familia de clientes"
              list={
                !realizandoPeticionUltimosClientes && ultimosClientesGestor
                  ? [
                      ...ultimosClientesGestor.map((cliente, index) => {
                        return {
                          ...cliente,
                          image: `/static/mock-images/avatars/avatar_${
                            20 + index
                          }.jpg`,
                        };
                      }),
                    ]
                  : []
              }
            />
          </Grid>

          <Grid item xs={12} md={12} lg={6}>
            <AppTaskFinished
              title="Últimas tareas finalizadas"
              subheader="Especiales"
              list={
                !realizandoPeticionHistorialTareas && historialTareas
                  ? historialTareas.tareasEspeciales
                  : []
              }
              loading={realizandoPeticionHistorialTareas}
            />
          </Grid>

          <Grid item xs={12} md={12} lg={6}>
            <AppTaskFinished
              title="Últimas tareas finalizadas"
              subheader="Planes"
              list={
                !realizandoPeticionHistorialTareas && historialTareas
                  ? historialTareas.tareasPlan
                  : []
              }
              loading={realizandoPeticionHistorialTareas}
            />
          </Grid>

          {/* <Grid item xs={12} md={12} lg={12}>
            <AppHighlyValuedTask
              title="Tareas más cotizados"
              subheader="Cantidad de clientes por Tarea"
              loading={realizandoPeticionTareasMasCotizados}
              chart={
                !realizandoPeticionTareasMasCotizados && tareasMasCotizadas
                  ? tareasMasCotizadas
                  : { series: [] }
              }
            />
          </Grid> */}
        </Grid>
      </Container>
      {modalEstadisticas && (
        <ModalEstadisticasFacturacionMensual
          isOpen={modalEstadisticas}
          setOpen={setModalEstadisticas}
          fechaInicio={dayjs(fechaInicio).format("YYYY/MM")}
          fechaTermino={dayjs(fechaFin).format("YYYY/MM")}
          obtenerSucursales={obtenerSucursalesParaFiltro}
          rut={usuario.rut}
        />
      )}
      {modalDetalleTareaSucursal && (
        <ModalDetalleTareaSucursal
          isOpen={modalDetalleTareaSucursal}
          setOpen={setModalDetalleTareaSucursal}
          dataTarjetaTotalTareas={detalleCantidadTareas}
          dataDistribucionTareas={tareasMensualesYAnualesAgrupadas}
          dataInformacionTareasInternas={informacionTareasInternas}
          dataGraficoEstadisticasTareas={estadisticasTareasEmpleado}
          cargandoDataGraficoEstadisticasTareas={
            obteniendoEstadisticasTareasEmpleado
          }
          cargandoInformacionTareasInternas={cargandoInformacionTareasInternas}
          cargandoDataDistribucionTareas={
            cargandoTareasMensualesYAnualesAgrupadas
          }
          cargandoDataTarjetaTotalTareas={cargandoDetalleCantidadTareas}
          rut={usuario.rut}
          fechaInicio={dayjs(fechaInicio).format("YYYY/MM")}
          fechaTermino={dayjs(fechaFin).format("YYYY/MM")}
          obtenerSucursales={obtenerSucursalesParaFiltro}
          sucursal={sucursal}
          filtroFecha={filtroFechaEstadisticas}
        />
      )}
      {abrirModalDetalleTareaPorEstado && (
        <ModalDetalleTareaPorEstado
          isOpen={abrirModalDetalleTareaPorEstado}
          setOpen={setAbrirModalDetalleTareaPorEstado}
          titulo={tituloModalDetalleTareasPorEstado}
          dataTable={tareasEmpleado}
          datosGraficoDispersion={dispersionTareasEmpleado}
          cargandoDatosGraficoDispersion={obteniendoDispersionTareasEmpleado}
          loadingDataTable={obteniendoTareasEmpleado}
        />
      )}
      {abrirModalDetalleClientes && (
        <ModalDistribucionClientes
          isOpen={abrirModalDetalleClientes}
          setOpen={setAbrirModalDetalleClientes}
          title="Detalle distribución clientes"
          cantidadPromedioPlanes={cantidadPlanesPromedioClientes}
          promedioFacturaClientes={promedioFacturasClientes}
          divisionClientes={clientesPorDivisionLazy}
          planesMasCotizados={planesMasCotizados}
          cargandoPlanesMasCotizados={obteniendoPlanesMasCotizados}
          cargandoDivisionClientes={obteniendoclientesPorDivision}
          cargandoPromedioFacturaClientes={obteniendoPromedioFacturasCliente}
          cargandoCantidadPromedioPlanes={
            obteniendoCantidadPlanesPromedioClientes
          }
          contribuyentesPlanes={contribuyentesPlanes}
          cargandoContribuyentesPlanes={obteniendocontribuyentesPlanes}
          filtros={{
            rut: usuario.rut,
            sucursales: obtenerSucursalesParaFiltro(),
            fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
            fechaFin: dayjs(fechaFin).format("YYYY/MM"),
          }}
        />
      )}
      {abrirModalDetalleEmpleados && (
        <ModalDistribucionEmpleados
          isOpen={abrirModalDetalleEmpleados}
          setOpen={setAbrirModalDetalleEmpleados}
          title="Detalle empleados"
          cargandoEmpleados={cargandoEmpleadosActivos}
          empleados={empleadosActivos || []}
        />
      )}
      {modalIngresos && (
        <ModalEstadisticasRecaudacion
          isOpen={modalIngresos}
          setOpen={setModalIngresos}
          fechaInicio={dayjs(fechaInicio).format("YYYY/MM")}
          fechaTermino={dayjs(fechaFin).format("YYYY/MM")}
          datosRecaudacion={facturacionDatos} // Pasamos la información ya descargada
          cargandoDatos={realizandoPeticionFacturacion} // Pasamos el estado de carga actual
        />
      )}
      {abrirModalDetalleResumenPropuestas && (
        <ModalDetalleResumenPropuestas
          isOpen={abrirModalDetalleResumenPropuestas}
          setOpen={setAbrirModalDetalleResumenPropuestas}
          title={tituloDetalleResumenPropuestas}
          propuestas={propuestasFiltradas || []}
          cargandoPropuestas={cargandoPropuestasFiltradas}
          estado={estadoSeleccionado}
          fechaInicio={fechaInicioExportacion}
          fechaFin={fechaFinExportacion}
        />
      )}
    </>
  );
};

export default Estadisticas;
